import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Star, X } from "lucide-react";
import { SmartLink } from "./SiteHeader";

const API = process.env.REACT_APP_BACKEND_URL;
const NEVER = ["/contact", "/client-login", "/portal", "/apply", "/partner-portal", "/admin", "/realtor-partners"];
const DWELL = 8000, EXIT = 450, FIRST = 8000, GAP = 45000, ROUTE_GRACE = 3000, PER_PAGE = 2, PER_SESSION = 3, QUIET_DAYS = 7;

const ls = { get: (k) => { try { return JSON.parse(localStorage.getItem(k)); } catch { return null; } }, set: (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch {} } };
const ss = { get: (k) => Number(sessionStorage.getItem(k) || 0), set: (k, v) => sessionStorage.setItem(k, String(v)) };

function blocked() {
  const a = document.activeElement;
  if (a && (a.tagName === "INPUT" || a.tagName === "TEXTAREA" || a.tagName === "SELECT" || a.isContentEditable)) return true;
  if (Date.now() - (window.__hmLastType || 0) < 60000) return true;
  if (document.querySelector('[role="dialog"], .calendly-overlay, .ck-modal, .rt-textme')) return true;
  if (window.visualViewport && window.innerWidth < 700 && window.visualViewport.height < window.innerHeight * 0.75) return true;
  if (document.hidden) return true;
  return false;
}

function Card({ c, onClose }) {
  const body = (
    <div className={`hp-card hp-${c.kind}`} role="status" data-testid={`hello-proof-${c.kind}`}>
      <button type="button" className="hp-x" aria-label="Dismiss" onClick={onClose} data-testid="hello-proof-dismiss"><X size={14} /></button>
      {c.kind === "review" ? (
        <>
          <div className="hp-avatar">{(c.name || "H")[0]}</div>
          <div className="hp-body">
            <div className="hp-head"><b>{c.name}</b>{c.location && <span> · {c.location}</span>}</div>
            <p>“{c.text}”</p>
            <div className="hp-google"><span className="hp-g">G</span> Google review <span className="hp-stars">{[0, 1, 2, 3, 4].map((i) => <Star key={i} size={12} fill="currentColor" />)}</span></div>
          </div>
        </>
      ) : (
        <>
          <div className={`hp-dot hp-dot-${c.kind}`} />
          <div className="hp-body"><p className="hp-lead">{c.text}</p>{c.sub && <div className="hp-sub">{c.sub}</div>}</div>
        </>
      )}
    </div>
  );
  return c.href ? <SmartLink href={c.href} className="hp-link" data-testid="hello-proof-link">{body}</SmartLink> : body;
}

export default function HelloProof() {
  const { pathname } = useLocation();
  const [card, setCard] = useState(null);
  const [leaving, setLeaving] = useState(false);
  const timers = useRef([]);
  useEffect(() => {
    const onKey = () => { window.__hmLastType = Date.now(); };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, []);
  useEffect(() => {
    setCard(null);
    timers.current.forEach(clearTimeout); timers.current = [];
    if (NEVER.some((p) => pathname.startsWith(p))) return;
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const quiet = ls.get("hm_proof_quiet"); if (quiet && Date.now() < quiet) return;
    if (ss.get("hm_proof_session") >= PER_SESSION) return;
    const seen = ls.get("hm_proof_seen") || {};
    const seenIds = Object.keys(seen).filter((k) => Date.now() - seen[k] < 30 * 864e5);
    const renewal = ls.get("hm_renewal")?.date;
    const kindOf = pathname.startsWith("/rates") || /\/mortgage-rates/.test(pathname) ? "rates" : null;
    let shown = 0, queue = [], scrolled = kindOf === "rates", cancelled = false;
    const onScroll = () => { scrolled = true; };
    window.addEventListener("scroll", onScroll, { passive: true });
    const m = pathname.match(/^\/(strategy-vault|calculators)\/([^/]+)\/?$/);
    if (m && window.__hmProofLogged !== pathname) { window.__hmProofLogged = pathname; fetch(`${API}/api/public/proof/event`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ kind: m[1] === "calculators" ? "calc_view" : "vault_view", slug: m[2] }) }).catch(() => {}); }
    const show = () => {
      if (cancelled || !queue.length || shown >= PER_PAGE || ss.get("hm_proof_session") >= PER_SESSION) return;
      if (!scrolled || blocked()) { timers.current.push(setTimeout(show, 2500)); return; }
      const c = queue.shift(); shown += 1;
      ss.set("hm_proof_session", ss.get("hm_proof_session") + 1);
      if (c.kind === "review") ls.set("hm_proof_seen", { ...(ls.get("hm_proof_seen") || {}), [c.id]: Date.now() });
      setLeaving(false); setCard(c);
      timers.current.push(setTimeout(() => { setLeaving(true); timers.current.push(setTimeout(() => { setCard(null); setLeaving(false); timers.current.push(setTimeout(show, GAP)); }, EXIT)); }, DWELL));
    };
    const t0 = setTimeout(() => {
      fetch(`${API}/api/public/proof?path=${encodeURIComponent(pathname)}&seen=${encodeURIComponent(seenIds.join(","))}${renewal ? `&renewal=${renewal}` : ""}`)
        .then((r) => (r.ok ? r.json() : null)).then((d) => { if (!d || cancelled) return; queue = d.cards || []; timers.current.push(setTimeout(show, Math.max(0, FIRST - ROUTE_GRACE))); }).catch(() => {});
    }, ROUTE_GRACE);
    timers.current.push(t0);
    const hide = () => { if (blocked()) { setLeaving(true); timers.current.push(setTimeout(() => { setCard(null); setLeaving(false); }, EXIT)); } };
    document.addEventListener("focusin", hide);
    return () => { cancelled = true; timers.current.forEach(clearTimeout); window.removeEventListener("scroll", onScroll); document.removeEventListener("focusin", hide); };
  }, [pathname]);
  if (!card) return null;
  const dismiss = (e) => { e.preventDefault(); e.stopPropagation(); ls.set("hm_proof_quiet", Date.now() + QUIET_DAYS * 864e5); setLeaving(true); timers.current.push(setTimeout(() => { setCard(null); setLeaving(false); }, EXIT)); };
  return <div className={`hp-wrap${leaving ? " hp-leaving" : ""}`} data-testid="hello-proof"><Card c={card} onClose={dismiss} /></div>;
}
